/**
 * QAScenarios — predefined QA test scenarios for Rush Fever 7s Deluxe.
 *
 * Each scenario bundles a human-readable name, description, category, and
 * the QAOverride partial that the QAController can apply in one shot.
 */

import { SymbolId } from '../config/GameConfig';
import type { QAOverride, QAScenarioId } from './QAOverrides';

// ── Symbol shorthands ───────────────────────────────────────────────────────

const W  = SymbolId.WILD;
const S  = SymbolId.SCATTER;
const R  = SymbolId.RUSH;
const Pa = SymbolId.PIC_A;
const Pb = SymbolId.PIC_B;
const Pc = SymbolId.PIC_C;
const Pd = SymbolId.PIC_D;
const Pe = SymbolId.PIC_E;
const A  = SymbolId.ACE;
const K  = SymbolId.KING;
const Q  = SymbolId.QUEEN;
const J  = SymbolId.JACK;
const T  = SymbolId.TEN;

// ── Public types ────────────────────────────────────────────────────────────

export interface QAScenario {
  id: QAScenarioId;
  name: string;
  description: string;
  category: 'feature' | 'win' | 'special';
  override: Partial<QAOverride>;
}

// ── Scenario definitions ────────────────────────────────────────────────────

export const QA_SCENARIOS: Map<QAScenarioId, QAScenario> = new Map([

  // ── Jackpot Pick scenarios ──────────────────────────────────────────────

  ['jackpotPick', {
    id: 'jackpotPick',
    name: 'Jackpot Pick (matching 7s)',
    description: 'Triggers the jackpot pick feature with WILD on reel 2. Pick results: 7-7-7 (matching tier 7).',
    category: 'feature',
    override: {
      forceFeature: 'jackpotPick',
      jpPicks: [7, 7, 7],
      forceGrid: [
        [Pa, A,  K ],
        [W,  Pa, T ],
        [Pb, A,  Pc],
        [T,  K,  A ],
        [A,  T,  K ],
      ],
    },
  }],

  ['jackpotPickTier9', {
    id: 'jackpotPickTier9',
    name: 'Jackpot Pick — Grand (Tier 9)',
    description: 'Jackpot pick with WILD on reel 2. Pick results: 9-9-6 — two 9s match for Grand jackpot.',
    category: 'feature',
    override: {
      forceFeature: 'jackpotPick',
      jpPicks: [9, 9, 6],
      forceGrid: [
        [Pa, A,  K ],
        [W,  Pa, T ],
        [Pb, A,  Pc],
        [T,  K,  A ],
        [A,  T,  K ],
      ],
    },
  }],

  ['jackpotPickNoMatch', {
    id: 'jackpotPickNoMatch',
    name: 'Jackpot Pick — No Match',
    description: 'Jackpot pick with WILD on reel 2. Pick results: 6-7-8 — all different, lowest tier wins.',
    category: 'feature',
    override: {
      forceFeature: 'jackpotPick',
      jpPicks: [6, 7, 8],
      forceGrid: [
        [Pa, A,  K ],
        [W,  Pa, T ],
        [Pb, A,  Pc],
        [T,  K,  A ],
        [A,  T,  K ],
      ],
    },
  }],

  // ── Free Games scenarios ────────────────────────────────────────────────

  ['freeGames', {
    id: 'freeGames',
    name: 'Free Games Trigger',
    description: 'Forces free-games trigger with SCATTER on reels 1, 3, and 5.',
    category: 'feature',
    override: {
      forceFeature: 'freeGames',
      forceGrid: [
        [S,  A,  K ],
        [Pa, Pb, T ],
        [K,  A,  S ],
        [T,  K,  Pc],
        [Pd, T,  S ],
      ],
    },
  }],

  ['freeGamesWithJP', {
    id: 'freeGamesWithJP',
    name: 'Free Games + Jackpot Pick',
    description: 'Free-games trigger (SCATTER on reels 1, 3, 5) with WILD on reel 2 so the first free spin can trigger a jackpot pick.',
    category: 'feature',
    override: {
      forceFeature: 'freeGames',
      jpPicks: [8, 8, 8],
      forceGrid: [
        [S,  A,  K ],
        [W,  Pb, T ],
        [K,  A,  S ],
        [T,  K,  Pc],
        [Pd, T,  S ],
      ],
    },
  }],

  // ── Rush Fever scenarios ────────────────────────────────────────────────

  ['rushFever3', {
    id: 'rushFever3',
    name: 'Rush Fever — 3 Symbols',
    description: 'Triggers Rush Fever with exactly 3 Rush symbols on the grid.',
    category: 'feature',
    override: {
      forceFeature: 'rushFever',
      rushCount: 3,
      forceGrid: [
        [R,  A,  K ],
        [Pa, R,  T ],
        [K,  A,  R ],
        [T,  K,  Pb],
        [Pc, T,  K ],
      ],
    },
  }],

  ['rushFever5', {
    id: 'rushFever5',
    name: 'Rush Fever — 5 Symbols',
    description: 'Triggers Rush Fever with 5 Rush symbols scattered across the grid.',
    category: 'feature',
    override: {
      forceFeature: 'rushFever',
      rushCount: 5,
      forceGrid: [
        [R,  R,  K ],
        [Pa, R,  T ],
        [R,  A,  R ],
        [T,  K,  Pb],
        [Pc, T,  K ],
      ],
    },
  }],

  ['rushFever7', {
    id: 'rushFever7',
    name: 'Rush Fever — 7 Symbols',
    description: 'Triggers Rush Fever with 7 Rush symbols for a large rush event.',
    category: 'feature',
    override: {
      forceFeature: 'rushFever',
      rushCount: 7,
      forceGrid: [
        [R,  R,  R ],
        [Pa, R,  T ],
        [R,  A,  R ],
        [T,  R,  Pb],
        [Pc, R,  K ],
      ],
    },
  }],

  ['rushFever9', {
    id: 'rushFever9',
    name: 'Rush Fever — 9 Symbols (Max)',
    description: 'Triggers Rush Fever with 9 Rush symbols — maximum possible rush event.',
    category: 'feature',
    override: {
      forceFeature: 'rushFever',
      rushCount: 9,
      forceGrid: [
        [R,  R,  R ],
        [R,  R,  R ],
        [R,  A,  R ],
        [T,  R,  R ],
        [Pa, T,  K ],
      ],
    },
  }],

  // ── Big Win scenarios ───────────────────────────────────────────────────

  ['bigWinAmazing', {
    id: 'bigWinAmazing',
    name: 'Big Win — Amazing (10x+)',
    description: 'Forces an Amazing-tier big win celebration (10x+ total bet). Win amount forced via multiplier, no grid override needed.',
    category: 'win',
    override: {
      forceFeature: 'bigWin',
      bigWinTier: 'amazing',
    },
  }],

  ['bigWinAwesome', {
    id: 'bigWinAwesome',
    name: 'Big Win — Awesome (25x+)',
    description: 'Forces an Awesome-tier big win celebration (25x+ total bet). Win amount forced via multiplier, no grid override needed.',
    category: 'win',
    override: {
      forceFeature: 'bigWin',
      bigWinTier: 'awesome',
    },
  }],

  ['bigWinIncredible', {
    id: 'bigWinIncredible',
    name: 'Big Win — Incredible (50x+)',
    description: 'Forces an Incredible-tier big win celebration (50x+ total bet). Win amount forced via multiplier, no grid override needed.',
    category: 'win',
    override: {
      forceFeature: 'bigWin',
      bigWinTier: 'incredible',
    },
  }],

  // ── Special / misc scenarios ────────────────────────────────────────────

  ['deadSpin', {
    id: 'deadSpin',
    name: 'Dead Spin (No Win)',
    description: 'Forces a guaranteed losing grid with all mismatched low-pay royal symbols.',
    category: 'special',
    override: {
      forceFeature: null,
      forceGrid: [
        [A,  K,  Q ],
        [K,  Q,  J ],
        [Q,  J,  T ],
        [J,  T,  A ],
        [T,  A,  K ],
      ],
    },
  }],

  ['smallWin', {
    id: 'smallWin',
    name: 'Small Win (3-of-a-kind)',
    description: 'Forces a small win with PIC_A on reels 1-3 (3-of-a-kind) and mismatched royals elsewhere.',
    category: 'special',
    override: {
      forceFeature: null,
      forceGrid: [
        [Pa, A,  K ],
        [Pa, K,  Q ],
        [Pa, Q,  J ],
        [T,  J,  A ],
        [K,  A,  T ],
      ],
    },
  }],

]);

// ── Lookup helpers ──────────────────────────────────────────────────────────

/**
 * Retrieve a single scenario by ID.
 * Returns `undefined` if the ID is not recognised.
 */
export function getScenario(id: QAScenarioId): QAScenario | undefined {
  return QA_SCENARIOS.get(id);
}

/**
 * Return all scenarios that belong to a given category
 * ('feature', 'win', or 'special').
 */
export function getScenariosByCategory(cat: string): QAScenario[] {
  const results: QAScenario[] = [];
  for (const scenario of QA_SCENARIOS.values()) {
    if (scenario.category === cat) {
      results.push(scenario);
    }
  }
  return results;
}
